<template>
	<!-- 十大股东 -->
	<MStateBox :loading="loading" :empty="isEmpty" class="act-container" ref="container">
		<van-pull-refresh v-model="isLoading" success-text="刷新成功" @refresh="onScrollPullDown">
			<div ref="layout">
				<div class="box-wrapper">
					<div class="dropdown-row">
						<m-dropdown :value.sync="dateValue" :options="dateList" @change="changeDrop"></m-dropdown>
						<div class="switch-wrap">
							<div
								class="switch-item"
								:class="{ 'switch-item--active': stockType === 1 }"
								@click="switchType(1)"
							>
								<span>{{ $t('enterprise.top-10-holders') }}</span>
							</div>
							<div
								class="switch-item"
								:class="{ 'switch-item--active': stockType === 2 }"
								@click="switchType(2)"
							>
								<span>{{ $t('enterprise.top-10-float-holders') }}</span>
							</div>
						</div>
					</div>
					<div v-html="renderTips" class="total-holdings-value"></div>
					<MiniTable :data="tableList">
						<MiniTableColumn prop="name" :label="i18n.t('enterprise.shareholder')" width="120" />
						<MiniTableColumn prop="holdings" :label="i18n.t('enterprise.number-of-shares-held')" width="80">
							<template #default="{ row }">
								<span class="font-table-barlow">{{ formatterColTransferUnit(row.holdings) }}</span>
							</template>
						</MiniTableColumn>
						<MiniTableColumn
							prop="holdingsRatio"
							:label="
								stockType === 1
									? i18n.t('enterprise.proportion-of-total-share-capital')
									: i18n.t('enterprise.percentage-of-total-circulating-share-capital')
							"
							width="86"
						>
							<template #default="{ row }">
								<span class="font-table-barlow">{{
									!row.holdingsRatio ? '-' : `${row.holdingsRatio.toFixed(2)}%`
								}}</span>
							</template>
						</MiniTableColumn>
						<MiniTableColumn prop="holdingsChangeRatio" :label="i18n.t('enterprise.QoQ-change')" width="80">
							<template #default="{ row }">
								<span class="font-table-barlow" :style="calcPercentageStyle(row.holdingsChangeRatio)">
									{{ formatPercentage(row.holdingsChangeRatio) }}
								</span>
							</template>
						</MiniTableColumn>
					</MiniTable>
				</div>
				<m-visible-tips fixed class="visible-background" />
			</div>
		</van-pull-refresh>
	</MStateBox>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { formatterColTransferUnit } from '@/utils/enterprise';
import { getRect } from '@/utils/api';
import { PullRefresh as VanPullRefresh } from 'vant';
import { ZH_HK, EN, ZH } from '@/assets/js/i18n';
import i18n from '@/assets/js/i18n';
import moment from 'moment';
import { useModuleData } from '@rebuild/hooks/data/useModuleData';
import MStateBox from '@rebuild/components/MStateBox.vue';
import { MiniTable, MiniTableColumn } from '@rebuild/components/table';

const { top_ten_shareholder, top_ten_shareholder_date } = useModuleData();

const isLoading = ref(false);
const renderTips = ref('');
const stockType = ref(1);
const dateValue = ref('');

const dateList = computed(() => {
	const result = top_ten_shareholder_date.data?.data || [];
	return result.map(v => ({
		text: v,
		value: moment(v).valueOf()
	}));
});

const tableList = computed(() => {
	const result = top_ten_shareholder.data?.data || {};
	return result.data || [];
});

const isEmpty = computed(() => {
	return top_ten_shareholder.empty || !dateList.value?.length;
});

async function loadData(value) {
	await top_ten_shareholder.refresh({
		timestamp: value,
		type: stockType.value
	});

	const result = top_ten_shareholder.data?.data || {};
	renderTips.value = translateText(result);
}

async function loadConfig() {
	await top_ten_shareholder_date.refresh({
		type: stockType.value
	});

	if (dateList.value.length > 0) {
		dateValue.value = dateList.value[0].value;
		await loadData(dateValue.value);
	}
	finish();
}

const loading = ref(true);
const finish = () => {
	loading.value = false;
};

async function onScrollPullDown() {
	await loadConfig();
	isLoading.value = false;
}

function changeDrop(value) {
	loadData(value);
}

function switchType(value) {
	if (stockType.value === value) return;
	stockType.value = value;
	loadConfig();
}

function formatPercentage(num) {
	if (num === 0) return i18n.t('enterprise.unchanged');
	if (num > 0) return `+${num.toFixed(2)}%`;
	return `${num.toFixed(2)}%`;
}

function calcPercentageStyle(num) {
	if (num > 0) return 'color: #EB5149';
	if (num < 0) return 'color: #44C970';
}

function translateText(result) {
	const values = [
		`${formatterColTransferUnit(result.totalHoldings)}`,
		stockType.value === 1 ? '占总股本' : '占总流通股本',
		stockType.value === 1 ? '佔總股本' : '佔總流通股本',
		`${result.totalHoldingsRatio.toFixed(2)}%`,
		calcPercentageStyle(result.totalHoldingsChangeRatio),
		formatPercentage(result.totalHoldingsChangeRatio)
	];

	const LocaleText = {
		[ZH]: values =>
			`总计持有<span>${values[0]}</span>股，${values[1]}<span>${values[3]}</span>，环比<span style="${values[4]};">${values[5]}</span>`,
		[EN]: values =>
			`Total of Top 10 Holdings: <span>${values[0]}</span>,<span>${values[3]}</span> of Top 10 Holdings, QoQ<span style="${values[4]};">${values[5]}</span>`,
		[ZH_HK]: values =>
			`總計持有<span>${values[0]}</span>股，${values[2]}<span>${values[3]}</span>，環比<span style="${values[4]};">${values[5]}</span>`
	};
	return LocaleText[i18n.locale](values);
}

const container = ref(null);
const layout = ref(null);

onMounted(async () => {
	await loadConfig();
	nextTick(() => {
		const containerRect = getRect(container.value);
		if (layout.value) {
			layout.value.style.minHeight = `${containerRect.height}px`;
		}
	});
});
</script>

<style lang="less" scoped>
.act-container {
	height: 100%;
	background: #fff;

	.box-wrapper {
		padding: 0.16rem 0.14rem;
		background: #fff;
	}

	.dropdown-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 0.14rem;

		.switch-wrap {
			display: flex;

			.switch-item {
				width: 0.88rem;
				height: 0.25rem;
				text-align: center;
				line-height: 0.25rem;
				border-radius: 0.18rem;
				background: var(--ir-theme-color-opacity-1);

				color: #666;
				font-family: 'PingFang SC';
				font-size: 0.12rem;
				font-style: normal;
				font-weight: 400;

				& + .switch-item {
					margin-left: 0.14rem;
				}
				&--active {
					font-weight: 500;
					color: var(--ir-theme-color);
				}
			}
		}
	}

	.total-holdings-value {
		margin-bottom: 0.14rem;
		color: #000;
		/* 12/B1-regular */
		font-family: 'PingFang SC';
		font-size: 0.12rem;
		font-style: normal;
		font-weight: 400;
		line-height: 140%; /* 16.8px */

		/deep/ span {
			color: var(--ir-theme-color);
			margin-left: 0.04rem;
		}
	}
}
</style>
